/**
 * YNS PDF Library
 * Handles PDF grid display, filtering, and owned/locked states.
 *
 * SECURITY MODEL (Triple-Layer):
 * 1. Random UUIDs - PDF IDs are unpredictable UUIDs (can't guess)
 * 2. Signed Access Token - HMAC-signed token from Shopify Liquid
 * 3. Expiring URLs - Supabase Edge Function returns 8-hour signed URLs
 *
 * Features:
 * - PDF grid rendering with owned/locked states
 * - Recently annotated section
 * - Async URL fetching via Supabase Edge Function
 * - Integration with PDF viewer
 * - Category filtering
 */

(function() {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  const CONFIG = {
    annotationsKey: 'yns_pdf_annotations',
    recentAnnotationsKey: 'yns_recent_annotations',
    urlCacheKey: 'yns_pdf_url_cache',
    selectors: {
      pdfGrid: '#pdfGrid',
      recentAnnotations: '#recentAnnotations',
      recentGrid: '#recentAnnotationsGrid',
      filters: '#pdfFilters',
      viewerModal: '#pdfViewerModal'
    }
  };

  // ============================================
  // State
  // ============================================
  let currentFilter = 'all';
  let annotations = {};
  let urlCache = {}; // Cache for signed URLs with expiry times
  let pendingUrlRequests = {}; // Track in-flight requests to prevent duplicates

  // Access is now determined by server-side Locksmith checks
  // These are populated from YNS_SECURE_DATA which only contains
  // URLs for content the user has purchased
  let customerAccess = {
    fundamentals: false,
    pharmacology: false,
    bundle: false
  };

  // ============================================
  // Initialization
  // ============================================
  function init() {
    // Get customer access from secure server-rendered data (Locksmith)
    // This is the authoritative source - URLs are only present if user has access
    const secureData = window.YNS_SECURE_DATA || {};
    if (secureData.access) {
      customerAccess.fundamentals = secureData.access.fundamentals || false;
      customerAccess.pharmacology = secureData.access.pharmacology || false;
      customerAccess.bundle = secureData.access.bundle || false;
    }

    // Fallback to YNS_CONFIG for backwards compatibility
    if (!secureData.access) {
      const config = window.YNS_CONFIG || {};
      if (config.access) {
        customerAccess.fundamentals = config.access.pdfFundamentals || false;
        customerAccess.pharmacology = config.access.pdfPharm || false;
        customerAccess.bundle = config.access.pdfBundle || false;
      }
    }

    // Load annotations and URL cache
    loadAnnotations();
    loadUrlCache();

    // Setup UI
    setupFilterListeners();
    setupBonusSectionListeners();
    renderPDFGrid();
    renderRecentAnnotations();

    // Switching between the NCLEX and ICU tracks re-scopes the book list
    document.addEventListener('yns:track', renderPDFGrid);
  }

  // ============================================
  // URL Cache Management
  // ============================================
  function loadUrlCache() {
    try {
      const stored = localStorage.getItem(CONFIG.urlCacheKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Filter out expired entries (keep entries valid for at least 30 more minutes)
        const now = Date.now();
        const thirtyMinutes = 30 * 60 * 1000;
        urlCache = {};
        Object.entries(parsed).forEach(([pdfId, entry]) => {
          if (entry.expiresAt > now + thirtyMinutes) {
            urlCache[pdfId] = entry;
          }
        });
      }
    } catch (e) {
      urlCache = {};
    }
  }

  function saveUrlCache() {
    try {
      localStorage.setItem(CONFIG.urlCacheKey, JSON.stringify(urlCache));
    } catch (e) {
      console.warn('Failed to save URL cache:', e);
    }
  }

  function getCachedUrl(pdfId) {
    const entry = urlCache[pdfId];
    if (!entry) return null;

    // Check if URL is still valid (at least 30 minutes remaining)
    const now = Date.now();
    const thirtyMinutes = 30 * 60 * 1000;
    if (entry.expiresAt < now + thirtyMinutes) {
      delete urlCache[pdfId];
      saveUrlCache();
      return null;
    }

    return entry.url;
  }

  function setCachedUrl(pdfId, url, expiresInSeconds) {
    urlCache[pdfId] = {
      url,
      expiresAt: Date.now() + (expiresInSeconds * 1000)
    };
    saveUrlCache();
  }

  // ============================================
  // Secure PDF URL Fetching
  // ============================================

  /**
   * Get a signed URL for a PDF from the Supabase Edge Function.
   * Uses the HMAC-signed token from Shopify Liquid for authentication.
   * Returns cached URL if still valid, otherwise fetches a new one.
   */
  async function getPdfUrl(pdfId) {
    // Check cache first
    const cachedUrl = getCachedUrl(pdfId);
    if (cachedUrl) {
      return cachedUrl;
    }

    // Check if there's already a pending request for this PDF
    if (pendingUrlRequests[pdfId]) {
      return pendingUrlRequests[pdfId];
    }

    // Get the token from Shopify Liquid
    const tokenData = window.YNS_PDF_TOKEN;
    if (!tokenData || !tokenData.payload || !tokenData.signature) {
      throw new Error('PDF access token not available. Please refresh the page.');
    }

    // Check if token has expired
    if (tokenData.exp < Date.now()) {
      throw new Error('PDF access token has expired. Please refresh the page.');
    }

    // Build the token string (payload.signature)
    const tokenString = tokenData.payload + '.' + tokenData.signature;

    // Get the API URL
    const apiUrl = window.YNS_PDF_API_URL;
    if (!apiUrl) {
      throw new Error('PDF API URL not configured.');
    }

    // Create the request promise and store it to prevent duplicate requests
    const requestPromise = (async () => {
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            pdfId,
            token: tokenString
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.error || `HTTP ${response.status}`;

          if (response.status === 401) {
            throw new Error('Access token invalid or expired. Please refresh the page.');
          }
          if (response.status === 403) {
            throw new Error('You do not have access to this PDF.');
          }
          if (response.status === 404) {
            throw new Error('PDF not found.');
          }
          throw new Error(`Failed to get PDF URL: ${errorMessage}`);
        }

        const data = await response.json();

        if (!data.url) {
          throw new Error('Invalid response from PDF service.');
        }

        // Cache the URL (default 8 hours = 28800 seconds)
        const expiresIn = data.expiresIn || 28800;
        setCachedUrl(pdfId, data.url, expiresIn);

        return data.url;
      } finally {
        // Clean up pending request
        delete pendingUrlRequests[pdfId];
      }
    })();

    pendingUrlRequests[pdfId] = requestPromise;
    return requestPromise;
  }

  // ============================================
  // Bonus Section Handlers
  // ============================================
  function setupBonusSectionListeners() {
    const bonusSection = document.querySelector('#bonusSection');
    if (!bonusSection) return;

    // Handle clicks on bonus "Open PDF" and "View PDF" buttons
    bonusSection.addEventListener('click', (e) => {
      const openBtn = e.target.closest('.yns-bonus-open-btn');
      const viewBtn = e.target.closest('.yns-bonus-view-btn');
      const btn = openBtn || viewBtn;

      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const pdfId = btn.dataset.pdfId;
      if (pdfId) {
        handlePdfClick(pdfId);
      }
    });

    // Also handle clicking on the card itself for premium cards
    bonusSection.querySelectorAll('.yns-bonus-card--premium').forEach(card => {
      card.addEventListener('click', (e) => {
        // Don't trigger if clicking on the button
        if (e.target.closest('.yns-bonus-open-btn')) return;

        const pdfId = card.dataset.bonusId;
        if (pdfId) {
          handlePdfClick(pdfId);
        }
      });
    });
  }

  // ============================================
  // Annotations Storage
  // ============================================
  function loadAnnotations() {
    try {
      const stored = localStorage.getItem(CONFIG.annotationsKey);
      annotations = stored ? JSON.parse(stored) : {};
    } catch (e) {
      annotations = {};
    }
    return annotations;
  }

  function getAnnotationsForPdf(pdfId) {
    return annotations[pdfId] || [];
  }

  function getAnnotationCount(pdfId) {
    const pdfAnnotations = annotations[pdfId] || [];
    return pdfAnnotations.length;
  }

  function getRecentlyAnnotatedPdfs(limit = 4) {
    const annotatedPdfs = [];

    Object.entries(annotations).forEach(([pdfId, pdfAnnotations]) => {
      if (pdfAnnotations.length > 0) {
        const latestAnnotation = pdfAnnotations.reduce((latest, current) => {
          return new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest;
        });

        annotatedPdfs.push({
          pdfId,
          annotationCount: pdfAnnotations.length,
          lastAnnotated: latestAnnotation.createdAt
        });
      }
    });

    // Sort by most recently annotated
    annotatedPdfs.sort((a, b) => new Date(b.lastAnnotated) - new Date(a.lastAnnotated));

    return annotatedPdfs.slice(0, limit);
  }

  // ============================================
  // Access Control (Server-Side via Locksmith)
  // ============================================

  /**
   * Check if user has access to a specific PDF.
   * This now reads from YNS_SECURE_DATA which is server-side rendered.
   * The hasAccess flag is set by Locksmith - there's nothing to bypass
   * because the URL is literally not in the HTML for unauthorized users.
   */
  function hasPdfAccess(pdfId) {
    const pdfData = window.YNS_PDF_DATA;
    if (!pdfData) return false;

    const pdf = pdfData.getPdfById(pdfId);
    if (!pdf) return false;

    // Primary check: Server-side determined access flag
    // This is set by Locksmith and cannot be bypassed
    if (pdf.hasAccess === true) return true;

    // Legacy fallback for backwards compatibility
    if (customerAccess.bundle) return true;
    if (pdf.category === 'fundamentals' && customerAccess.fundamentals) return true;
    if (pdf.category === 'pharmacology' && customerAccess.pharmacology) return true;

    return false;
  }

  /**
   * Get the secure file URL for a PDF (sync method for backwards compat).
   * Note: This returns cached URL or null. Use getPdfUrl() for async fetching.
   */
  function getSecureFileUrl(pdfId) {
    // First check cache
    const cachedUrl = getCachedUrl(pdfId);
    if (cachedUrl) return cachedUrl;

    // Fallback to legacy fileUrl if present
    const pdfData = window.YNS_PDF_DATA;
    if (pdfData) {
      return pdfData.getFileUrl(pdfId);
    }

    return null;
  }

  function getCustomerTags() {
    const tags = [];
    if (customerAccess.bundle) tags.push('yns_pdf_bundle');
    if (customerAccess.fundamentals) tags.push('yns_pdf_fundamentals');
    if (customerAccess.pharmacology) tags.push('yns_pdf_pharm');
    return tags;
  }

  // ============================================
  // PDF Grid Rendering
  // ============================================
  function renderPDFGrid() {
    const pdfGrid = document.querySelector(CONFIG.selectors.pdfGrid);
    if (!pdfGrid) return;

    const pdfData = window.YNS_PDF_DATA;
    if (!pdfData) {
      console.warn('YNS PDF Data not loaded');
      pdfGrid.innerHTML = '<p>Loading books...</p>';
      return;
    }

    // Get all PDFs and filter out bonus items (they're displayed in the bonus section)
    let pdfs = pdfData.getAllPdfs().filter(pdf => !pdf.isBonus);

    // ICU track: only the ICU kit books (ICU Survival Book, ECG, Critical Care Meds)
    if (window.YNSTrack && window.YNSTrack() === 'icu') {
      const icuKit = ((window.YNS_SECURE_DATA && window.YNS_SECURE_DATA.pdfs) || []).filter(p => p.icuKit).map(p => p.id);
      pdfs = pdfs.filter(pdf => icuKit.includes(pdf.id));
    }

    // Apply filter
    if (currentFilter !== 'all') {
      if (currentFilter === 'owned') {
        pdfs = pdfs.filter(pdf => hasPdfAccess(pdf.id));
      } else {
        pdfs = pdfs.filter(pdf => pdf.category === currentFilter);
      }
    }

    if (pdfs.length === 0) {
      pdfGrid.innerHTML = `
        <div class="yns-no-results">
          <p>No books found.</p>
          ${currentFilter === 'owned' ?
            '<a href="/collections/study-guides">Browse available books</a>' :
            '<button class="yns-filter-reset">Show all books</button>'}
        </div>
      `;

      const resetBtn = pdfGrid.querySelector('.yns-filter-reset');
      if (resetBtn) {
        resetBtn.addEventListener('click', () => {
          currentFilter = 'all';
          updateFilterUI();
          renderPDFGrid();
        });
      }
      return;
    }

    // Render PDF cards
    pdfGrid.innerHTML = pdfs.map((pdf, index) => createPDFCard(pdf, index)).join('');

    // Add click handlers
    pdfGrid.querySelectorAll('.yns-pdf-card').forEach(card => {
      card.addEventListener('click', () => {
        const pdfId = card.dataset.pdfId;
        handlePdfClick(pdfId);
      });
    });
  }

  function createPDFCard(pdf, index) {
    // For platform access items like Q-Bank, use hasAccess directly from the data
    const hasAccess = pdf.isPlatformAccess ? pdf.hasAccess : hasPdfAccess(pdf.id);
    const annotationCount = pdf.isPlatformAccess ? 0 : getAnnotationCount(pdf.id);
    const delay = Math.min(index * 0.05, 0.3);

    const badgeHtml = hasAccess ?
      `<span class="yns-pdf-badge owned">&#10003; In your library</span>` :
      `<span class="yns-pdf-badge locked">&#128274; Locked</span>`;

    const annotationHtml = annotationCount > 0 && hasAccess ?
      `<span class="yns-pdf-annotation-count">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
        </svg>
        ${annotationCount}
      </span>` : '';

    // Price display for locked cards
    const priceHtml = !hasAccess && pdf.price ? `
      <div class="yns-pdf-price">
        ${pdf.compareAtPrice ? `<span class="yns-compare-price">${pdf.compareAtPrice}</span>` : ''}
        <span class="yns-current-price">${pdf.price}</span>
      </div>
    ` : '';

    // Lock overlay - use purchaseUrl directly for platform access items
    const unlockUrl = pdf.purchaseUrl || (window.YNS_PDF_DATA ? window.YNS_PDF_DATA.getUnlockUrl(pdf.id) : '#');
    const lockOverlayHtml = !hasAccess ? `
      <div class="yns-pdf-lock-overlay">
        <div class="yns-lock-icon-large">Not in your library yet</div>
        ${priceHtml}
        <a href="${unlockUrl}" class="yns-btn-unlock-sm" onclick="event.stopPropagation();">Unlock <span aria-hidden="true">&rarr;</span></a>
      </div>
    ` : '';

    // Different icon for platform access (Q-Bank) vs PDF
    const iconHtml = pdf.isPlatformAccess ?
      `<div class="yns-pdf-icon yns-qbank-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>` :
      `<div class="yns-pdf-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14,2 14,8 20,8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
      </div>`;

    return `
      <div class="yns-pdf-card ${hasAccess ? '' : 'locked'}${pdf.isPlatformAccess ? ' yns-platform-access' : ''}"
           data-pdf-id="${pdf.id}"
           data-category="${pdf.category}"
           style="animation-delay: ${delay}s;">
        <div class="yns-pdf-thumbnail">
          ${pdf.thumbnail ?
            `<img src="${pdf.thumbnail}" alt="${pdf.title}" loading="lazy">` :
            iconHtml}
          ${badgeHtml}
          ${lockOverlayHtml}
        </div>
        <div class="yns-pdf-content">
          <span class="yns-pdf-category">${formatCategory(pdf.category)}</span>
          <h3 class="yns-pdf-title">${pdf.title}</h3>
          <p class="yns-pdf-description">${pdf.description || ''}</p>
        </div>
      </div>
    `;
  }

  function formatCategory(category) {
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // ============================================
  // Recent Annotations Section
  // ============================================
  function renderRecentAnnotations() {
    const recentSection = document.querySelector(CONFIG.selectors.recentAnnotations);
    const recentGrid = document.querySelector(CONFIG.selectors.recentGrid);

    if (!recentSection || !recentGrid) return;

    const recentPdfs = getRecentlyAnnotatedPdfs(4);

    if (recentPdfs.length === 0) {
      recentSection.style.display = 'none';
      return;
    }

    recentSection.style.display = 'block';

    const pdfData = window.YNS_PDF_DATA;
    if (!pdfData) return;

    recentGrid.innerHTML = recentPdfs.map(item => {
      const pdf = pdfData.getPdfById(item.pdfId);
      if (!pdf) return '';

      const timeAgo = getRelativeTime(item.lastAnnotated);

      return `
        <div class="yns-recent-card" data-pdf-id="${item.pdfId}">
          <div class="yns-recent-thumbnail">
            ${pdf.thumbnail ?
              `<img src="${pdf.thumbnail}" alt="${pdf.title}">` :
              `<div class="yns-pdf-icon-sm">PDF</div>`}
          </div>
          <div class="yns-recent-info">
            <h4>${pdf.title}</h4>
            <p>${item.annotationCount} annotations - ${timeAgo}</p>
          </div>
        </div>
      `;
    }).join('');

    // Add click handlers
    recentGrid.querySelectorAll('.yns-recent-card').forEach(card => {
      card.addEventListener('click', () => {
        const pdfId = card.dataset.pdfId;
        handlePdfClick(pdfId);
      });
    });
  }

  function getRelativeTime(timestamp) {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  }

  // ============================================
  // Filter Logic
  // ============================================
  function setupFilterListeners() {
    const filters = document.querySelector(CONFIG.selectors.filters);
    if (!filters) return;

    filters.addEventListener('click', (e) => {
      const pill = e.target.closest('.yns-filter-pill');
      if (!pill) return;

      currentFilter = pill.dataset.filter;
      updateFilterUI();
      renderPDFGrid();
    });
  }

  function updateFilterUI() {
    const filters = document.querySelector(CONFIG.selectors.filters);
    if (!filters) return;

    filters.querySelectorAll('.yns-filter-pill').forEach(pill => {
      pill.classList.toggle('active', pill.dataset.filter === currentFilter);
    });
  }

  // ============================================
  // PDF Click Handler (Async with Edge Function)
  // ============================================

  /**
   * Show loading state on a PDF card
   */
  function showPdfLoadingState(pdfId) {
    const card = document.querySelector(`[data-pdf-id="${pdfId}"]`);
    if (card) {
      card.classList.add('loading');
      // Add loading spinner overlay
      const thumbnail = card.querySelector('.yns-pdf-thumbnail');
      if (thumbnail && !thumbnail.querySelector('.yns-pdf-loading')) {
        const spinner = document.createElement('div');
        spinner.className = 'yns-pdf-loading';
        spinner.innerHTML = '<div class="yns-spinner"></div>';
        thumbnail.appendChild(spinner);
      }
    }
  }

  /**
   * Hide loading state on a PDF card
   */
  function hidePdfLoadingState(pdfId) {
    const card = document.querySelector(`[data-pdf-id="${pdfId}"]`);
    if (card) {
      card.classList.remove('loading');
      const spinner = card.querySelector('.yns-pdf-loading');
      if (spinner) spinner.remove();
    }
  }

  /**
   * Show an error toast notification
   */
  function showErrorToast(message) {
    // Use existing toast if available
    let toast = document.querySelector('.yns-error-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'yns-error-toast';
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('visible');

    setTimeout(() => {
      toast.classList.remove('visible');
    }, 4000);
  }

  /**
   * Show Q-Bank access popup with instructions
   */
  function showQBankPopup(pdf) {
    // Remove any existing popup
    const existingPopup = document.querySelector('.yns-qbank-popup-overlay');
    if (existingPopup) {
      existingPopup.remove();
    }

    const overlay = document.createElement('div');
    overlay.className = 'yns-qbank-popup-overlay';

    const popup = document.createElement('div');
    popup.className = 'yns-qbank-popup';

    // Format the instructions with proper line breaks
    const formattedInstructions = pdf.popupContent.instructions
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');

    popup.innerHTML = `
      <button class="yns-qbank-popup-close" aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
      <div class="yns-qbank-popup-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      </div>
      <h2 class="yns-qbank-popup-title">${pdf.popupContent.title}</h2>
      <div class="yns-qbank-popup-content">
        <p>${formattedInstructions}</p>
      </div>
      <a href="${pdf.platformUrl}" target="_blank" rel="noopener noreferrer" class="yns-qbank-popup-btn">
        Go to ExamASAP
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
          <polyline points="15 3 21 3 21 9"></polyline>
          <line x1="10" y1="14" x2="21" y2="3"></line>
        </svg>
      </a>
    `;

    overlay.appendChild(popup);
    document.body.appendChild(overlay);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Close handlers
    const closePopup = () => {
      overlay.classList.add('closing');
      setTimeout(() => {
        overlay.remove();
        document.body.style.overflow = '';
      }, 200);
    };

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closePopup();
      }
    });

    popup.querySelector('.yns-qbank-popup-close').addEventListener('click', closePopup);

    // ESC key to close
    const escHandler = (e) => {
      if (e.key === 'Escape') {
        closePopup();
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);

    // Animate in
    requestAnimationFrame(() => {
      overlay.classList.add('visible');
    });
  }

  /**
   * Handle PDF click - fetch signed URL and open viewer
   */
  async function handlePdfClick(pdfId) {
    const pdfData = window.YNS_PDF_DATA;
    if (!pdfData) {
      console.warn('PDF data not available');
      return;
    }

    const pdf = pdfData.getPdfById(pdfId);
    if (!pdf) {
      console.warn('PDF not found:', pdfId);
      return;
    }

    // Check access using server-side determined flag
    // The hasAccess flag is set by Locksmith - cannot be bypassed
    if (!pdf.hasAccess) {
      // Redirect to product page for locked PDFs
      const unlockUrl = pdf.purchaseUrl || pdfData.getUnlockUrl(pdfId);
      window.location.href = unlockUrl;
      return;
    }

    // Handle platform access (Q-Bank) - show popup instead of opening PDF
    if (pdf.isPlatformAccess && pdf.popupContent) {
      showQBankPopup(pdf);
      return;
    }

    // Show loading state
    showPdfLoadingState(pdfId);

    try {
      // Fetch signed URL from Edge Function
      const fileUrl = await getPdfUrl(pdfId);

      if (!fileUrl) {
        throw new Error('Failed to get PDF URL');
      }

      // Open PDF viewer with the secure signed URL
      if (window.YNSPdfViewer) {
        window.YNSPdfViewer.openPdf(pdfId, fileUrl);
      } else {
        // Fallback: open in new tab
        console.warn('PDF Viewer not loaded, opening in new tab');
        window.open(fileUrl, '_blank');
      }

      // Log activity
      document.dispatchEvent(new CustomEvent('yns:pdfOpened', {
        detail: {
          pdfId,
          pdfTitle: pdf.title
        },
        bubbles: true
      }));

    } catch (error) {
      console.error('Failed to open PDF:', error);
      showErrorToast(error.message || 'Failed to open PDF. Please try again.');

      // If token expired, suggest refresh
      if (error.message && error.message.includes('expired')) {
        setTimeout(() => {
          if (confirm('Your session has expired. Would you like to refresh the page?')) {
            window.location.reload();
          }
        }, 500);
      }
    } finally {
      hidePdfLoadingState(pdfId);
    }
  }

  // ============================================
  // Public API
  // ============================================
  window.YNSPdfLibrary = {
    open: handlePdfClick, // same flow as clicking a book card (loading state, access check, error toast)
    refresh: () => {
      renderPDFGrid();
      renderRecentAnnotations();
    },
    setFilter: (filter) => {
      currentFilter = filter;
      updateFilterUI();
      renderPDFGrid();
    },
    getFilter: () => currentFilter,
    hasPdfAccess,
    getSecureFileUrl,
    getPdfUrl, // Async method to fetch signed URL
    getAnnotationCount,
    getRecentlyAnnotatedPdfs,
    clearUrlCache: () => {
      urlCache = {};
      localStorage.removeItem(CONFIG.urlCacheKey);
    }
  };

  // ============================================
  // Initialize
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-render when tab changes to PDFs
  document.addEventListener('yns:tabchange', (e) => {
    if (e.detail.currentTab === 'pdfs') {
      renderPDFGrid();
      renderRecentAnnotations();
    }
  });

})();

// Add styles for recent annotations section and loading states
const recentStyles = document.createElement('style');
recentStyles.textContent = `
  .yns-recent-annotations {
    margin-bottom: var(--yns-space-2xl);
  }

  .yns-recent-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: var(--yns-space-md);
  }

  .yns-recent-card {
    display: flex;
    align-items: center;
    gap: var(--yns-space-md);
    padding: var(--yns-space-md);
    background: var(--yns-white);
    border: 1px solid var(--yns-sage-border);
    border-radius: var(--yns-radius-md);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .yns-recent-card:hover {
    border-color: var(--yns-sage);
    box-shadow: var(--yns-shadow-md);
  }

  .yns-recent-thumbnail {
    width: 48px;
    height: 64px;
    background: var(--yns-cream-dark);
    border-radius: var(--yns-radius-sm);
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .yns-recent-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .yns-pdf-icon-sm {
    font-size: 0.75rem;
    color: var(--yns-text-muted);
  }

  .yns-recent-info {
    flex: 1;
    min-width: 0;
  }

  .yns-recent-info h4 {
    font-family: var(--yns-font-display);
    font-size: 0.9375rem;
    font-weight: 600;
    margin: 0 0 4px 0;
    color: var(--yns-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .yns-recent-info p {
    font-size: 0.75rem;
    color: var(--yns-text-muted);
    margin: 0;
  }

  .yns-no-results {
    grid-column: 1 / -1;
    text-align: center;
    padding: var(--yns-space-2xl);
    color: var(--yns-text-secondary);
  }

  .yns-no-results a,
  .yns-filter-reset {
    color: var(--yns-sage);
    text-decoration: underline;
    cursor: pointer;
    background: none;
    border: none;
    font-size: inherit;
  }

  .yns-pdf-badge.info {
    background: var(--yns-gold-soft);
    color: var(--yns-gold-warm);
  }

  /* Loading state styles */
  .yns-pdf-card.loading {
    pointer-events: none;
    opacity: 0.7;
  }

  .yns-pdf-loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: inherit;
  }

  .yns-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--yns-sage-border, #e0e0e0);
    border-top-color: var(--yns-sage, #46523E);
    border-radius: 50%;
    animation: yns-spin 0.8s linear infinite;
  }

  @keyframes yns-spin {
    to { transform: rotate(360deg); }
  }

  /* Error toast styles */
  .yns-error-toast {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%) translateY(100px);
    background: #ef4444;
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    z-index: 10000;
    opacity: 0;
    transition: all 0.3s ease;
    max-width: 90%;
    text-align: center;
  }

  .yns-error-toast.visible {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
`;
document.head.appendChild(recentStyles);

